import { generateForm } from "@modules/react-jsonschema-form-bootstrap";
import validator from "@rjsf/validator-ajv8";
import { useState, useEffect, useMemo, useRef } from "react";
import { Alert } from "react-bootstrap";
import { api } from "../api-client";
import { useProviderProfileFormConfig } from "../forms/ProviderProfile";
import { ServiceProviderType, StateType, ServiceType, ServiceProvidertypeType, LanguageType, UserType } from "../types/appointment-types";
import { LegacyUserType } from "@/apps/common/api-client/api-client.types";
import { useRequest } from "ahooks";
import { useAuthStore } from "@/apps/common/store/useAuthStore";
import AsyncMultiSelectWidget from "../components/form-widgets/AsyncMultiSelectWidget"
import { Splitter } from "@fullcalendar/core/internal";
import UserSettings from "@/apps/admin/components/UserSettings";
import _ from 'lodash';

export type ServiceProviderProfileType = {
  //type: string;
  //user: UserType | null;
  firstName: string,
  lastName: string,
  phoneNumber: string,
  email: string,
  state: number,
  status: "pending" | "approved" | null;
  about: string;
  address: string;
  qualification?: string;
  states: StateType[];
  referralFees?: number;
  fullProfileLink?: string;
  //serviceTypes?: ServiceType[];
  serviceTypes?: string[];
  npi?: string;
  tags?: string[];
  //service_provider_types?: ServiceProvidertypeType[]
  service_provider_types?: string[];
  languages?: LanguageType[],
}

type SchemaProps = {
  mode: "register" | "admin" | "create" | "edit" | "preview";
  spType: "non-coaching" | "coaching"
}
const Form = generateForm<Partial<ServiceProviderProfileType>>();

export default function ProviderProfileForm({
  mode,
  serviceProvider,
  updateData,
  providerProfileFormData
}: {
  mode: "register" | "admin" | "create" | "edit" | "preview";
  serviceProvider: Partial<ServiceProviderType> | null;
  updateData: (data: any) => void;
  providerProfileFormData: (data: any) => void;
  // display: (value: boolean) => void;
}) {

  //const [serviceProviderData, setServiceProviderData] = useState<Partial<ServiceProviderType>>();
  const [serviceProviderData, setServiceProviderData] = useState<Partial<ServiceProviderProfileType>>();
  const [submitMessage, setSubmitMessage] = useState<string>();
  const [coachingServiceProviderTypes, setCoachingServiceProviderTypes] = useState<Partial<ServiceProvidertypeType>[]>();
  const [serviceProviderTypes, setServiceProviderTypes] = useState<Partial<ServiceProvidertypeType>[]>();
  const [serviceTypes, setServiceTypes] = useState<Partial<ServiceType>[]>();
  const [coachingServiceTypes, setCoachingServiceTypes] = useState<Partial<ServiceType>[]>();
  const [displaySubmitMessage, setDisplaySubmitMessage] = useState<boolean>(false);
  const [props, setProps] = useState<SchemaProps>({ mode: mode, spType: "non-coaching" })

  const serviceProviderDataRef = useRef<Partial<ServiceProviderProfileType>>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    state: 0,
    status: null,
    about: '',
    address: '',
    qualification: '',
    states: undefined,
    referralFees: 0,
    fullProfileLink: '',
    serviceTypes: [],
    npi: '',
    tags: [],
    service_provider_types: [],
    languages: undefined
  });

  const { schema, uiSchema, customValidate, transformErrors } =
    useProviderProfileFormConfig(props);
  // useProviderProfileFormConfig({
  //   mode
  // });

  const [formSchema, setFormSchema] = useState<any>(schema)
  const [currentUiSchema, setCurrentUiSchema] = useState<any>(uiSchema)

  const NON_THERAPY_SERVICEPROVIDER_TYPES = [
    "Health and Wellness Coach",
    "Health and Wellness Peer Advocate",
    "Parent Child Coach"]

  const THERAPY_SERVICEPROVIDER_TYPES = [
    "Licensed Clinical Psychologist",
    "Licensed Clinical Social Worker",
    "Licensed Marriage and Family Therapist",
    "Licensed Professional Counselor"
  ]

  const NON_THERAPY_SERVICES = [
    "Health and Wellness Coaching",
    "Health and Wellness Peer Support",
    "Parent-Child Goal-Setting Coaching"
  ]

  const THERAPY_SERVICES = [
    "Counseling and Therapy for Mental Health",
    "Trauma Counseling",
    "Psychological Evaluation for General Mental Health",
    "Psychological Evaluation for Surgical Clearance",
    "Pain Counseling and Evaluation",
    "Medication Titration Counseling",
  ]

  const showAlert = () => {
    setDisplaySubmitMessage(true)
    const timeoutId = setTimeout(() => {
      setDisplaySubmitMessage(false);
    }, 2000);
  }

  ////console.debug ('service_provider_types - ' + JSON.stringify(serviceProvider?.service_provider_types))
  const setValues = () => {
    ////console.debug('tags type - ' + typeof serviceProvider?.tags + JSON.stringify(serviceProvider?.tags))

    const serviceProviderTypes: string[] = [];
    serviceProvider?.service_provider_types?.forEach((providerType) => {
      if (providerType?.name)
        if (providerType?.enabled === true)
          serviceProviderTypes.push(providerType?.name)
    })

    const providerServiceTypes: string[] = [];
    serviceProvider?.serviceTypes?.forEach((serviceType) => {
      if (serviceType.enabled === true)
        providerServiceTypes.push(serviceType.name)
    })

    ////console.debug (`saved serviceTypes - ${JSON.stringify(serviceTypes)}`)

    const data = {
      ...(serviceProvider
        ? {
          //...serviceProvider,
          firstName: serviceProvider?.user?.firstName,
          lastName: serviceProvider?.user?.lastName,
          serviceType: serviceProvider?.type === 'mental-health-coach' ? 'Health and Wellness Coach' :
            serviceProvider?.type === 'health-wellness-peer' ? 'Health and Wellness Peer' :
              serviceProvider?.type === 'parent-child-coach' ? 'Parent Child Coach' : 'Mental Health Therapist',
          phoneNumber: serviceProvider?.user?.phoneNumber,
          email: serviceProvider?.user?.email,
          state: serviceProvider?.user?.state?.id,
          npi: serviceProvider?.npi ? serviceProvider?.npi : '',
          status: serviceProvider.status ? serviceProvider.status : "pending",
          states: serviceProvider.states,
          //serviceTypes: serviceProvider.serviceTypes,
          //service_provider_types: serviceProvider?.service_provider_types,
          serviceTypes: providerServiceTypes,
          service_provider_types: serviceProviderTypes,
          languages: serviceProvider?.languages,
          address: serviceProvider?.address ? serviceProvider?.address : '',
          about: serviceProvider?.about ? serviceProvider?.about : '',
          fullProfileLink: serviceProvider?.fullProfileLink ? serviceProvider?.fullProfileLink : '',
          //tags: serviceProvider?.tags ? serviceProvider?.tags.join(',') : ''
          tags: serviceProvider?.tags ? serviceProvider?.tags : []
        }
        : {}
      )
    };

    ////console.debug('setting values ' + JSON.stringify(data))
    return data
  }

  const { loading: saving, runAsync: updateUser } = useRequest(
    api.auth.updateUser.bind(api.auth),
    {
      onSuccess: (result, params) => {
        try {
          //console.debug("user update succeeded - " + JSON.stringify(result));
          useAuthStore.getState().fetchUser();
        } catch (error) {
          //console.debug("user update failed - " + JSON.stringify(error));
          console.error(error);
        }
      },
      manual: true
    }
  );

  async function fetchEnabledServiceTypes() : Promise<ServiceType[]>{
    console.debug (`fetchEnabledServiceTypes`)
    return await api.common.fetchServiceTypes().then(
      (res) => {
        return res
        //setServiceTypes(res)
      }
    )
  }

  async function fetchEnabledServiceProviderTypes () : Promise<ServiceProvidertypeType[]>  {
    console.debug (`fetchEnabledServiceProviderTypes`)
    return await api.common.fetchServiceProviderTypes().then(
      (res) => {
        return res
      }
    )
  }

  useEffect(() => {
    console.debug(`useEffect[]`)
    ////console.debug(`schema - ${JSON.stringify(uiSchema)}`)
    fetchEnabledServiceTypes().then((res)=>{setServiceTypes(res)})
    fetchEnabledServiceProviderTypes().then ( (res)=>(setServiceProviderTypes(res)))

    const newUiSchema = uiSchema
    if ((serviceProviderData?.service_provider_types) &&
      (THERAPY_SERVICEPROVIDER_TYPES.includes(serviceProviderData?.service_provider_types?.[0]))) {
      //console.debug('initializing for therapy')
      newUiSchema.serviceTypes["ui:widget"] = "checkboxes"
      newUiSchema.npi["ui:widget"] = "text"
      newUiSchema.states["ui:widget"] = AsyncMultiSelectWidget
    }

    setCurrentUiSchema(newUiSchema)
    setFormSchema(formSchema)

  }, []);

  useEffect(() => {

    let newSchema = formSchema;
    newSchema = _.cloneDeep(formSchema);

    if ((serviceTypes) && (serviceTypes?.length > 1)) {
      console.debug (`Adding enabled ServiceTypes to the FORM Schema`)
 
      

      const _serviceTypes: string[] = []

      ////console.debug(`All enabled serviceTypes - ${JSON.stringify(serviceTypes)}`)
      serviceTypes?.forEach((serviceType) => {
        ////console.debug(`serviceType - ${serviceType?.name}`)
        if (serviceType?.name)
          _serviceTypes.push(serviceType?.name)
      })

      //newSchema.properties.serviceTypes.items.enum = _serviceTypes
      newSchema.properties['serviceTypes']['items']['enum'] = _serviceTypes


      if ((serviceProviderTypes) && (serviceProviderTypes?.length > 0)) {
         console.debug (`Adding enabled ServiceProviderTypes to the FORM Schema`)
        const _serviceProviderTypes: string[] = []

        //console.debug(`All enabled serviceProviderTypes - ${JSON.stringify(_serviceProviderTypes)}`)
        serviceProviderTypes?.forEach((serviceProviderType) => {
          //console.debug(`serviceProviderType - ${serviceProviderType?.name}`)
          if (serviceProviderType?.name)
            _serviceProviderTypes.push(serviceProviderType?.name)
        })
        newSchema.properties['service_provider_types']['items']['enum'] = _serviceProviderTypes
      }

      setFormSchema(newSchema)
      //setReRender(reRender+1)
    }

  }, [serviceTypes, serviceProviderTypes])


  // useEffect(() => {

  //   if ((serviceProviderTypes) && (serviceProviderTypes?.length > 0)) {
  //     const newSchema = schema

  //     const _serviceProviderTypes: string[] = []

  //     //console.debug(`All enabled serviceProviderTypes - ${JSON.stringify(_serviceProviderTypes)}`)
  //     serviceProviderTypes?.forEach((serviceProviderType) => {
  //       //console.debug(`serviceProviderType - ${serviceProviderType?.name}`)
  //       if (serviceProviderType?.name)
  //         _serviceProviderTypes.push(serviceProviderType?.name)
  //     })
  //     newSchema.properties['service_provider_types']['items']['enum'] = _serviceProviderTypes
  //     setFormSchema(newSchema)
  //   }

  // }, [serviceProviderTypes])



  useMemo(() => {
    //console.debug(`useMemo`)
    const serviceProviderType = api.common.fetchServiceProviderTypesforCoaching().then(
      (res => {
        ////console.debug(`********* serviceProviderType ${JSON.stringify(res)}`)
        setCoachingServiceProviderTypes(res)
      }
      )
    )
    const serviceType = api.common.fetchCoachingServiceType().then(
      (res) => {
        setCoachingServiceTypes(res)
      }
    )

  }, []);


  useEffect(() => {
    console.debug(`serviceProvider changed`)
    setFormSchema(formSchema)
    setCurrentUiSchema(uiSchema)
    setServiceProviderData(setValues())
  }, [serviceProvider, formSchema]);

  const onSubmit = (form: any) => {

    const formData = form?.formData;
    if (!formData?.npi) {
      formData.npi = ''
    }

    // Convert tags to JSON Object
    // if (formData?.tags) {
    //   //const tags: string[] = formData?.tags.split(",");
    //   ////console.debug(`tags - ${JSON.stringify(formData?.tags)}`)
    //   //formData.tags = tags
    // }

    ////console.debug(`serviceTypes - ${JSON.stringify(formData.serviceTypes)}`)

    const _serviceProviderTypes: Partial<ServiceProvidertypeType>[] = [];
    const _serviceTypes: Partial<ServiceType>[] = [];

    formData.service_provider_types.forEach((serviceProviderType: any) => {

      //console.debug(`serviceProviderType - ${JSON.stringify(serviceProviderType)}`)

      if (serviceProviderType === 'Health and Wellness Coach') {
        //console.debug('Health and Wellness Coach')

        const wellnessCoach = coachingServiceProviderTypes?.find((serviceProvider) =>
          serviceProvider.name === 'Health and Wellness Coach'
        )
        //formData.service_provider_types = [wellnessCoach]
        if (wellnessCoach)
          _serviceProviderTypes.push(wellnessCoach)
        formData['type'] = "mental-health-coach"
        const wellnessCoaching = coachingServiceTypes?.find((serviceType) => "Health and Wellness Coaching" === serviceType.name)
        if (wellnessCoaching)
          _serviceTypes.push(wellnessCoaching)
        //formData.serviceTypes = [wellnessCoaching]
        formData['qualification'] = 'Health and Wellness Coach'
      }
      else if (serviceProviderType === 'Health and Wellness Peer Advocate') {
        //console.debug('Health and Wellness Peer Advocate')
        const peerAdvocate = coachingServiceProviderTypes?.find((serviceProvider) =>
          serviceProvider.name === 'Health and Wellness Peer Advocate'
        )
        //formData.service_provider_types = [peerAdvocate]
        if (peerAdvocate)
          _serviceProviderTypes.push(peerAdvocate)
        const peerAdvocacy = coachingServiceTypes?.find((serviceType) => "Health and Wellness Peer Support" === serviceType.name)
        //formData.serviceTypes = [peerAdvocacy]
        if (peerAdvocacy)
          _serviceTypes.push(peerAdvocacy)
        //formData.service_provider_types = coachingServiceProviderType
        formData.type = "health-wellness-peer"
        //formData.serviceTypes = coachinServiceTypes
        formData['qualification'] = 'Health and Wellness Peer Advocate'
      }
      else if (serviceProviderType === 'Parent Child Coach') {
        //console.debug('Parent Child Coach')
        const parentChildCoach = serviceProviderTypes?.find((serviceProvider) =>
          serviceProvider.name === 'Parent Child Coach'
        )
        //formData.service_provider_types = [parentChildCoach]
        if (parentChildCoach) {
          //console.debug(`Adding Parent Child Coach`)
          _serviceProviderTypes.push(parentChildCoach)
        }

        const parentChildCoaching = serviceTypes?.find((serviceType) => {

          if ("Parent-Child Goal-Setting Coaching" === serviceType.name) {
            //console.debug(`serviceType - ${JSON.stringify(serviceType)}`)
            return serviceType
          }
        }
        )
        //formData.serviceTypes = [parentChildCoaching]

        if (parentChildCoaching) {
          //console.debug(`parentChildCoaching - ${JSON.stringify(parentChildCoaching)}`)
          _serviceTypes.push(parentChildCoaching)
        }
        formData['type'] = "parent-child-coach"
        //formData.serviceTypes = coachinServiceTypes
        formData['qualification'] = 'Parent Child Coach'
      }
      // else {
      //   //console.debug('Therapy Service Providers')
      //   formData?.service_provider_types?.forEach ((pt:string)=>{
      //     const _pt = serviceProviderTypes?.find ((spt)=> spt.name === pt)
      //     if (_pt) {
      //       _serviceProviderTypes.push(_pt)
      //     }
      //   })


      // }
    })

    if (_serviceProviderTypes.length === 0) {

      //console.debug('Therapy Service Providers')
      formData.type = "licensed-therapist"


      formData?.service_provider_types?.forEach((pt: string) => {
        //console.debug(`Adding - ${pt}`)
        const _pt = serviceProviderTypes?.find((spt) => spt.name === pt)
        if (_pt) {
          _serviceProviderTypes.push(_pt)
          formData.qualification = _pt.name
        }
      })
    }

    if (_serviceProviderTypes.length > 0) {
      //console.debug(`Multiple Service Provider Types - ${JSON.stringify(_serviceProviderTypes)}`)
      formData.service_provider_types = _serviceProviderTypes
    }

    if (_serviceTypes.length === 0) {

      //console.debug('Therapy Service(s)')
      formData?.serviceTypes?.forEach((stf: string) => {
        //console.debug(`serviceType - ${stf}`)
        const _st = serviceTypes?.find((st) => st.name === stf)
        if (_st) {
          _serviceTypes.push(_st)
        }
      })
    }

    if (_serviceTypes.length > 0)
      formData.serviceTypes = _serviceTypes

    //console.debug('submitting Provider Profile data - ' + JSON.stringify(form.formData))

    if (mode === "register") {
      providerProfileFormData(formData)
      //console.debug(`Provider Profile Form - ${JSON.stringify(formData)}`)
      return
    }

    if (serviceProvider?.id) {

      ////console.debug('formdata.state' + JSON.stringify(formData.state))
      const updateUserData: Partial<LegacyUserType> = {

        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        username: formData.email,
        state: formData.state
      };
      //console.debug('Updating user data')
      updateUser(serviceProvider.user!.id, updateUserData).then(
        (res) => {

        },
        (err) => {
          setSubmitMessage("An error occured while updating the Provider details. Please try again.")
          showAlert()
        }
      )

      //api.serviceProviders.update(serviceProvider.id, form.formData).then(
      api.serviceProviders.update(serviceProvider.id, formData).then(
        (res) => {

          ////console.debug('updateUserData -' + JSON.stringify(updateUserData))
          if (res.id !== undefined) {
            api.serviceProviders.find(res.id)?.then(
              (resData) => {
                //console.debug('successfully updated service provider profile - ' + JSON.stringify(resData))
                setSubmitMessage("Provider details were updated successfully !")
                showAlert()
                updateData(resData)
                const serviceProviderTypes: string[] = [];
                resData?.service_provider_types?.forEach((providerType) => {
                  if (providerType?.name)
                    serviceProviderTypes.push(providerType?.name)
                })

                const serviceTypes: string[] = [];
                resData?.serviceTypes?.forEach((serviceType) => {
                  serviceTypes.push(serviceType.name)
                })
                let a: Partial<ServiceProviderProfileType>
                a = {
                  ...resData,
                  serviceTypes: serviceTypes,
                  service_provider_types: serviceProviderTypes
                }
                a.firstName = updateUserData.firstName
                a.lastName = updateUserData.firstName
                a.phoneNumber = updateUserData.phoneNumber
                a.email = updateUserData.email
                a.state = updateUserData.state?.id
                //setServiceProviderData(resData)
                setServiceProviderData(a)
              }
            )
          }
        },
        (err) => {
          ////console.debug('error updating service provider profile - ' + JSON.stringify(err)) 
          setSubmitMessage("An error occured while updating the Provider details. Please try again.")
          showAlert()
        }
      )
    }
  };

  const getUpdatedUiSchema = (hide: boolean) => {
    const newUiSchema = { ...currentUiSchema };

    if (hide === false) {
      //console.debug(`Displaying Therapy Services Fields`)
      // newUiSchema.serviceTypes["ui:disabled"] = false
      // newUiSchema.serviceTypes["ui:readonly"] = false
      newUiSchema.serviceTypes["ui:widget"] = "checkboxes"
      newUiSchema.npi["ui:widget"] = "text"
      newUiSchema.states["ui:widget"] = AsyncMultiSelectWidget
    }
    else {
      // newUiSchema.serviceTypes["ui:disabled"] = true
      // newUiSchema.serviceTypes["ui:readonly"] = true
      newUiSchema.serviceTypes["ui:widget"] = "hidden"
      newUiSchema.npi["ui:widget"] = "hidden"
      newUiSchema.states["ui:widget"] = "hidden"
    }

    return newUiSchema
  };

  function difference<T>(arr1: T[], arr2: T[]): T[] {
    return arr1.filter(item => !arr2.includes(item));
  }

  const handleFormChange = (data: any) => {

    ////console.debug(`For change - ${JSON.stringify(Object.keys(data))}`)
    const formData = data.formData
    ////console.debug(`For change - ${JSON.stringify(formData)}`)
    ////console.debug(`serviceProviderDataRef - ${JSON.stringify(JSON.stringify(serviceProviderDataRef.current.service_provider_types))}`)

    const service_provider_types = formData.service_provider_types

    const newServiceProviderTypes = []

    if (service_provider_types?.length === 0) {
      //console.debug(`No Service Provider Type selected`)
      /**
       * Unselect Service Types too
       */

      const newFormData = {
        ...formData,
        service_provider_types: [],
        serviceTypes: []
      }

      setServiceProviderData(newFormData)
      serviceProviderDataRef.current = { ...newFormData }
      const updatedUiSchema = getUpdatedUiSchema(false);
      setCurrentUiSchema(updatedUiSchema)
      return;

    }

    let newServiceProviderType;

    if (service_provider_types.length === 1) {
      //console.debug(`Selecting the first Service Provider Type`)
      newServiceProviderType = service_provider_types[0]
    }

    else if ((serviceProviderDataRef.current.service_provider_types) &&
      (service_provider_types?.length > serviceProviderDataRef.current.service_provider_types?.length)) {

      //console.debug(`Change in Service Provider Types`)

      const newSPTypes = difference(service_provider_types, serviceProviderDataRef.current.service_provider_types)
      newServiceProviderType = newSPTypes[0]

      //console.debug(`New SP Types - ${JSON.stringify(newSPTypes)}`)

    }

    if (NON_THERAPY_SERVICEPROVIDER_TYPES.includes(newServiceProviderType)) {

      //console.debug(`Non Therapy Service Provider Types - ${JSON.stringify(newServiceProviderType)}`)

      /**
       * Unselect all service provider types.
       * Find the service provider type that was added in the last change
       */
      const serviceTypes: string[] = [];
      switch (newServiceProviderType) {

        case "Health and Wellness Coach":
          serviceTypes.push("Health and Wellness Coaching")
          break;

        case "Health and Wellness Peer Advocate":
          serviceTypes.push("Health and Wellness Peer Support")
          break;

        case "Parent Child Coach":
          serviceTypes.push("Parent-Child Goal-Setting Coaching")
          break;
      }
      const newFormData = {
        ...formData,
        service_provider_types: [newServiceProviderType],
        serviceTypes: serviceTypes
      }

      serviceProviderDataRef.current = { ...newFormData }
      setServiceProviderData(newFormData)
      const updatedUiSchema = getUpdatedUiSchema(true);
      updatedUiSchema["spType"] = "coaching"
      setProps({ mode: mode, spType: "coaching" })
      setCurrentUiSchema(updatedUiSchema)
    }
    else if (THERAPY_SERVICEPROVIDER_TYPES.includes(newServiceProviderType)) {

      //console.debug(`Selecting Therapy Service Type Providers - ${JSON.stringify(service_provider_types)}`)
      const newSPTypes = difference(service_provider_types, NON_THERAPY_SERVICEPROVIDER_TYPES)
      const newServiceTypes = difference(formData.serviceTypes, NON_THERAPY_SERVICES)
      //console.debug(`newSPTypes - ${JSON.stringify(newSPTypes)}`)

      /**
       * Enable only certain ServiceTypes
       */

      const newFormData = {
        ...formData,
        service_provider_types: newSPTypes,
        serviceTypes: newServiceTypes
      }

      serviceProviderDataRef.current = { ...newFormData }
      setServiceProviderData(newFormData)
      const updatedUiSchema = getUpdatedUiSchema(false);
      updatedUiSchema["spType"] = "non-coaching"
      setCurrentUiSchema(updatedUiSchema)

    }

  };

  return (
    <>
      {
        <>
          <br />
          {((serviceProvider || mode === "register") &&
            (serviceTypes && serviceTypes?.length > 1) &&
            (serviceProviderTypes && serviceProviderTypes?.length > 0)) ?
            (<Form
              formContext={serviceProviderData}
              formData={serviceProviderData as Partial<ServiceProviderProfileType>}
              //schema={schema as any}
              //uiSchema={uiSchema as any}
              schema={formSchema}
              uiSchema={currentUiSchema}
              validator={validator}
              showErrorList={false}
              customValidate={customValidate}
              noHtml5Validate
              liveValidate={false}
              transformErrors={transformErrors}
              onSubmit={onSubmit}
              onChange={handleFormChange}
            ></Form>) : null
          }
          <br />
          <br />
          <Alert variant="secondary" className="mb-0 d-md-block d-sm-none" show={displaySubmitMessage} >
            {submitMessage}
          </Alert>
          {serviceProvider?.user?.id && (
            <UserSettings userId={serviceProvider?.user?.id} />
          )}
        </>

      }
    </>

  );

}
